import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Warning } from 'shared/assets/icons';
import { PriorityPickerMenu } from 'shared/components/ui/priority-picker-menu/priority-picker-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { TaskPriorityOptions } from 'shared/types/task-base';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu'; // Assuming this import is needed
import { MetaInteraction } from '../meta-interaction/meta-interaction';

export type MetaPriorityProps = {
  value?: TaskPriorityOptions | null;
  onChange: (value: TaskPriorityOptions | null) => void;
};

const OPEN_MENU_KEYS = ['KeyP'];

const PRIORITY_SHORTCUT_KEYS = [['Digit1'], ['Digit2'], ['Digit3'], ['Digit4']];

export const MetaPriority: React.FC<MetaPriorityProps> = ({
  value,
  onChange,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { t } = useTranslation();

  const onClear = () => {
    onChange(null);
    closeMenu();
  };

  useKeysDown(
    OPEN_MENU_KEYS,
    (e) => {
      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    { ignoreInputFields: true, capture: true },
  );

  const onPriorityShortcut = useCallback(
    (e: KeyboardEvent, keys: string[]) => {
      if (!e.shiftKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (keys.includes('Digit1')) {
        onChange(TaskPriorityOptions.High);
      }
      if (keys.includes('Digit2')) {
        onChange(TaskPriorityOptions.Medium);
      }
      if (keys.includes('Digit3')) {
        onChange(TaskPriorityOptions.Low);
      }
      if (keys.includes('Digit4')) {
        onChange(TaskPriorityOptions.None);
      }
    },
    [onChange],
  );

  useKeysDown(PRIORITY_SHORTCUT_KEYS, onPriorityShortcut, {
    ignoreInputFields: true,
    capture: true,
    otherKeysInCombinationAllowed: true,
  });

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Warning}
        placeholder={t('dialogs.task-detail.meta.priority.placeholder')}
        label={value ? t(`task.priority.options.${value}.label`) : undefined}
        onClick={openMenu}
        onClear={value ? onClear : undefined}
        selected={menuOpen}
      />

      {menuOpen && (
        <PriorityPickerMenu
          referenceElement={buttonRef}
          onClose={closeMenu}
          value={value}
          onChange={onChange}
        />
      )}
    </>
  );
};
